import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { tasksQuery, useCompleteTask, useCreateTask, type CrmTask } from '../lib/crm.js';

const TYPE_LABELS: Record<string, string> = {
  call: 'Anruf',
  email: 'E-Mail',
  meeting: 'Termin',
  todo: 'Aufgabe',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'niedrig',
  normal: 'normal',
  high: 'hoch',
};

export function TasksPage() {
  const [mine, setMine] = useState(true);
  const [showDone, setShowDone] = useState(false);

  const tasks = useQuery(tasksQuery({ mine, status: showDone ? 'done' : 'open' }));
  const complete = useCompleteTask();
  const create = useCreateTask();

  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [type, setType] = useState('todo');
  const [priority, setPriority] = useState('normal');

  const items = tasks.data?.items ?? [];
  const overdue = items.filter((t) => t.overdue);
  const rest = items.filter((t) => !t.overdue);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (title.trim().length === 0) return;
    create.mutate({
      title: title.trim(),
      type,
      priority,
      // datetime-local liefert Ortszeit ohne Zone; der Server erwartet ISO.
      dueAt: due ? new Date(due).toISOString() : null,
    });
    setTitle('');
    setDue('');
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Aufgaben</h1>
          <p className="muted text-sm">
            Überfällig heißt überfällig — die Marke kommt vom Server, nicht aus der Uhr des
            Browsers.
          </p>
        </div>
      </header>

      <div className="card">
        <div className="card__body stack">
          <form className="task-form" onSubmit={submit}>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Was ist zu tun?"
              aria-label="Titel der Aufgabe"
            />
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Art"
            >
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              aria-label="Priorität"
            >
              {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="datetime-local"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              aria-label="Fällig am"
            />
            <Button type="submit" loading={create.isPending}>
              Anlegen
            </Button>
          </form>

          <div className="filter-row">
            <label className="check">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
              Nur meine
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
              />
              Erledigte zeigen
            </label>
          </div>
        </div>
      </div>

      {tasks.isLoading && <p className="muted">Wird geladen …</p>}

      {!tasks.isLoading && items.length === 0 && (
        <div className="card">
          <div className="card__body">
            <p className="muted">
              {showDone ? 'Noch nichts erledigt.' : 'Nichts offen. Angenehm.'}
            </p>
          </div>
        </div>
      )}

      {overdue.length > 0 && (
        <TaskGroup
          heading={`Überfällig (${overdue.length})`}
          tasks={overdue}
          onComplete={(id) => complete.mutate(id)}
          busy={complete.isPending}
        />
      )}

      {rest.length > 0 && (
        <TaskGroup
          heading={showDone ? 'Erledigt' : 'Anstehend'}
          tasks={rest}
          onComplete={(id) => complete.mutate(id)}
          busy={complete.isPending}
          readOnly={showDone}
        />
      )}
    </div>
  );
}

function TaskGroup({
  heading,
  tasks,
  onComplete,
  busy,
  readOnly,
}: {
  heading: string;
  tasks: CrmTask[];
  onComplete: (id: string) => void;
  busy: boolean;
  readOnly?: boolean;
}) {
  return (
    <section className="card">
      <div className="card__body stack">
        <h2 style={{ fontSize: 'var(--text-lg)' }}>{heading}</h2>
        <ul className="tasks">
          {tasks.map((task) => (
            <li key={task.id} data-overdue={task.overdue ? 'true' : undefined}>
              <div>
                <strong>{task.title}</strong>
                <div className="muted text-sm">
                  {TYPE_LABELS[task.type] ?? task.type}
                  {task.priority !== 'normal' && ` · ${PRIORITY_LABELS[task.priority]}`}
                  {task.dueAt && ` · ${new Date(task.dueAt).toLocaleString('de-CH')}`}
                  {task.dealId && (
                    <>
                      {' · '}
                      <Link to="/crm/$id" params={{ id: task.dealId }}>
                        zum Deal
                      </Link>
                    </>
                  )}
                  {task.contactId && (
                    <>
                      {' · '}
                      <Link to="/kontakte/$id" params={{ id: task.contactId }}>
                        zum Kontakt
                      </Link>
                    </>
                  )}
                </div>
              </div>
              {!readOnly && (
                <Button variant="ghost" onClick={() => onComplete(task.id)} loading={busy}>
                  Erledigt
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
